import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { httpClient } from '@/shared/api/httpClient';
import { endpoints } from '@/shared/api/endpoints';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import { getErrorMessage } from '@/shared/api/errors';

export type NotificationItem = {
  _id: string;
  title?: string;
  body?: string;
  type?: string;
  key?: string;
  isRead?: boolean;
  createdAt?: string;
};

const PAGE_SIZE = 20;

export type NotificationPage = {
  data: NotificationItem[];
  pages: number;
  total?: number;
  unreadCount?: number;
};

type RawNotificationResponse = {
  data?:
    | NotificationItem[]
    | {
        data?: NotificationItem[];
        notifications?: NotificationItem[];
        pages?: number;
        totalPages?: number;
        total?: number;
        unreadCount?: number;
        pagination?: {
          pages?: number;
          totalPages?: number;
          total?: number;
          totalCount?: number;
        };
      };
  pages?: number;
  totalPages?: number;
  total?: number;
  unreadCount?: number;
  pagination?: {
    pages?: number;
    totalPages?: number;
    total?: number;
    totalCount?: number;
  };
};

function normalizeNotificationPage(
  raw: RawNotificationResponse,
): NotificationPage {
  const nested = raw?.data;
  const items = Array.isArray(nested)
    ? nested
    : nested?.data ?? nested?.notifications ?? [];

  const pagination =
    (!Array.isArray(nested) ? nested?.pagination : undefined) ?? raw?.pagination;

  const total =
    (!Array.isArray(nested) ? nested?.total : undefined) ??
    raw?.total ??
    pagination?.total ??
    pagination?.totalCount;

  const pages = Number(
    (!Array.isArray(nested)
      ? nested?.pages ?? nested?.totalPages
      : undefined) ??
      raw?.pages ??
      raw?.totalPages ??
      pagination?.pages ??
      pagination?.totalPages ??
      (total ? Math.ceil(Number(total) / PAGE_SIZE) : 0),
  );

  const unreadCount =
    (!Array.isArray(nested) ? nested?.unreadCount : undefined) ??
    raw?.unreadCount;

  return {
    data: items,
    pages: pages || 1,
    total,
    unreadCount,
  };
}

function getNextNotificationPageParam(
  lastPage: NotificationPage,
  allPages: NotificationPage[],
) {
  if (lastPage.data.length < PAGE_SIZE) return undefined;
  if (lastPage.pages > 1 && allPages.length >= lastPage.pages) return undefined;
  return allPages.length + 1;
}

export function useNotificationUnreadCount(enabled = true) {
  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: async () => {
      const { data } = await httpClient.get(endpoints.notifications.list, {
        params: { page: 1, limit: 1, isRead: false },
      });
      const page = normalizeNotificationPage(data as RawNotificationResponse);
      if (typeof page.unreadCount === 'number') return page.unreadCount;
      return page.total ?? page.data.length ?? 0;
    },
    enabled,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

export function useNotificationsInfinite(tab: 'all' | 'unread') {
  return useInfiniteQuery<NotificationPage>({
    queryKey: [...queryKeys.notifications.list(), tab],
    queryFn: async ({ pageParam }) => {
      const params: Record<string, unknown> = {
        page: pageParam,
        limit: PAGE_SIZE,
      };
      if (tab === 'unread') params.isRead = false;
      const { data } = await httpClient.get(endpoints.notifications.list, {
        params,
      });
      return normalizeNotificationPage(data as RawNotificationResponse);
    },
    getNextPageParam: getNextNotificationPageParam,
    initialPageParam: 1,
    refetchOnWindowFocus: false,
  });
}

export function useMarkReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await httpClient.patch(endpoints.notifications.markRead, {
        notificationId: id,
      });
      return data as { message?: string };
    },
    onSuccess: (res) => {
      toast.success(res?.message || 'Marked as read');
      void qc.invalidateQueries({ queryKey: queryKeys.notifications.root });
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed')),
  });
}

export function useMarkAllReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await httpClient.patch(
        endpoints.notifications.markAllRead,
      );
      return data as { message?: string };
    },
    onSuccess: (res) => {
      toast.success(res?.message || 'All marked as read');
      void qc.invalidateQueries({ queryKey: queryKeys.notifications.root });
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed')),
  });
}
